'use client';

import React, { useState, ReactNode, useRef, useId, useCallback } from 'react';
import { Upload, AlertCircle, CheckCircle, Loader } from 'lucide-react';
import { Modal, Button, Card, CardContent } from './ui';
import { parseCSV } from '../utils/csvUtils';
import toast from 'react-hot-toast';

export interface ImportRow {
  rowNumber: number;
  data: Record<string, string>;
  error?: string;
  isValid: boolean;
}

export interface ImportFailure {
  rowNumber: number;
  error: string;
  /** Short row label, e.g. camp or lesson name */
  label?: string;
}

/** Returned by {@link ImportModalProps.onImport} so the modal can show a summary before closing */
export interface ImportResult {
  created: number;
  failed: number;
  failures?: ImportFailure[];
}

export interface ImportContext {
  reportProgress: (processed: number, total: number) => void;
}

interface ImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Return counts to show the completion summary; return `undefined` to stay on the file step (e.g. total API failure). */
  onImport: (rows: ImportRow[], ctx?: ImportContext) => Promise<ImportResult | undefined>;
  title: string;
  formatGuide: ReactNode;
  requiredColumns?: string[];
  description?: string;
  /** Shown on the full-modal importing overlay (e.g. "Importing lessons…") */
  importingLabel?: string;
  /** Modal max width - defaults to '3xl' for import forms with format guide and preview */
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl';
  /** Optional id on the modal panel (e.g. tour highlight). */
  panelId?: string;
  /** Row-level validation after required-column checks (e.g. enum fields). Return an error message to mark the row invalid. */
  additionalRowError?: (row: Record<string, string>) => string | undefined;
}

export const ImportModal: React.FC<ImportModalProps> = ({
  isOpen,
  onClose,
  onImport,
  title,
  formatGuide,
  requiredColumns = [],
  description,
  importingLabel = 'Importing…',
  maxWidth = '5xl',
  panelId,
  additionalRowError,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [rows, setRows] = useState<ImportRow[]>([]);
  const [importing, setImporting] = useState(false);
  const [showFormatGuide, setShowFormatGuide] = useState(false);
  const [importComplete, setImportComplete] = useState(false);
  const [importStats, setImportStats] = useState({ created: 0, failed: 0 });
  const [importFailures, setImportFailures] = useState<ImportFailure[]>([]);
  const [importProgress, setImportProgress] = useState({ processed: 0, total: 0 });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const fileInputId = useId();

  const openFilePicker = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileSelect = async (selectedFile: File) => {
    setFile(selectedFile);
    setImportComplete(false);
    setImportStats({ created: 0, failed: 0 });
    setImportFailures([]);
    setImportProgress({ processed: 0, total: 0 });

    try {
      const data = await parseCSV(selectedFile);
      
      // Validate rows
      const validatedRows: ImportRow[] = data.map((row, index) => {
        const missingColumns = requiredColumns.filter(col => !row[col] || row[col].trim() === '');
        const extraErr = missingColumns.length === 0 ? additionalRowError?.(row) : undefined;
        const errorMsg =
          missingColumns.length > 0
            ? `Missing required columns: ${missingColumns.join(', ')}`
            : extraErr;

        return {
          rowNumber: index + 2, // +2 because row 1 is headers, index is 0-based
          data: row,
          error: errorMsg,
          isValid: missingColumns.length === 0 && !extraErr,
        };
      });

      setRows(validatedRows);
    } catch (error: any) {
      toast.error(`Failed to parse CSV: ${error.message}`);
      setFile(null);
      setRows([]);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles[0]) {
      handleFileSelect(droppedFiles[0]);
    }
  };

  const handleImportClick = async () => {
    if (!file || rows.length === 0) {
      toast.error('Please select a file with at least one valid row');
      return;
    }

    setImporting(true);
    const totalRows = rows.length;
    setImportProgress({ processed: 0, total: totalRows });
    try {
      const result = await onImport(rows, {
        reportProgress: (processed, total) => {
          setImportProgress({ processed, total });
        },
      });
      if (result) {
        setImportStats({ created: result.created, failed: result.failed });
        setImportFailures(result.failures ?? []);
        setImportComplete(true);
      }
    } catch (error: any) {
      toast.error(`Import failed: ${error.message}`);
    } finally {
      setImporting(false);
    }
  };

  const validRowCount = rows.filter(r => r.isValid).length;
  const invalidRowCount = rows.filter(r => !r.isValid).length;

  const handleClose = () => {
    if (!importing) {
      setFile(null);
      setRows([]);
      setImportComplete(false);
      setImportStats({ created: 0, failed: 0 });
      setImportFailures([]);
      setImportProgress({ processed: 0, total: 0 });
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={title} maxWidth={maxWidth} panelId={panelId}>
      <div className={`relative ${importing ? 'min-h-[14rem]' : 'min-h-[8rem]'}`}>
        {importing && (
          <div
            className="absolute inset-0 z-30 flex flex-col items-center justify-center gap-4 rounded-lg bg-white/92 dark:bg-gray-800/96 backdrop-blur-md px-6 animate-in fade-in-0 duration-200"
            aria-busy="true"
            aria-live="polite"
          >
            <div
              className="h-14 w-14 rounded-full border-[3px] border-gray-200 dark:border-gray-600 border-t-blue-600 dark:border-t-blue-400 animate-spin"
              aria-hidden
            />
            <div className="text-center space-y-1">
              <p className="text-base font-semibold text-gray-900 dark:text-white">{importingLabel}</p>
              {importProgress.total > 0 ? (
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Processing row {Math.min(importProgress.processed + 1, importProgress.total)} of{' '}
                  {importProgress.total}
                </p>
              ) : null}
              <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm">
                Please wait — this may take a moment for larger files.
              </p>
            </div>
          </div>
        )}

        {description && (
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">{description}</p>
        )}

        {!importComplete ? (
          <>
            <button
              onClick={() => setShowFormatGuide(!showFormatGuide)}
              className="mb-4 text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline"
            >
              {showFormatGuide ? '▼ Hide' : '▶ Show'} Format Guide
            </button>

            {showFormatGuide && (
              <Card className="mb-4 border-blue-200 dark:border-blue-900/50 bg-blue-50 dark:bg-blue-900/20">
                <CardContent className="p-4 text-sm text-gray-700 dark:text-gray-300">
                  {formatGuide}
                </CardContent>
              </Card>
            )}

            {!file ? (
              <div
                role="presentation"
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onClick={(e) => {
                  const target = e.target as HTMLElement;
                  if (!target.closest('button')) {
                    openFilePicker();
                  }
                }}
                className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center mb-4 hover:border-blue-400 dark:hover:border-blue-500 transition-colors cursor-pointer"
              >
                <Upload className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-3 pointer-events-none" />
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 pointer-events-none">
                  Drag and drop your CSV file here, or click to select
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-4 pointer-events-none">
                  Only .csv files are supported
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  id={fileInputId}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) handleFileSelect(f);
                    e.target.value = '';
                  }}
                  className="sr-only"
                />
                <Button
                  type="button"
                  aria-label="Choose CSV file to import"
                  onClick={(e) => {
                    e.stopPropagation();
                    openFilePicker();
                  }}
                >
                  Select File
                </Button>
              </div>
            ) : (
              <div className="mb-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {file.name}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {rows.length} rows • {validRowCount} valid • {invalidRowCount} invalid
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setFile(null);
                    setRows([]);
                  }}
                  disabled={importing}
                  className="text-sm text-blue-600 dark:text-blue-400 hover:underline mt-2 disabled:opacity-50"
                >
                  Change file
                </button>
              </div>
            )}

            {rows.length > 0 && (
              <div className="mb-4">
                <p className="text-sm font-medium text-gray-900 dark:text-white mb-3">
                  Preview ({rows.length} rows)
                </p>
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                  {rows.slice(0, 10).map((row) => (
                    <div
                      key={row.rowNumber}
                      className={`p-3 border-b border-gray-200 dark:border-gray-700 last:border-b-0 flex items-start gap-3 ${
                        row.isValid
                          ? 'bg-white dark:bg-gray-800'
                          : 'bg-red-50 dark:bg-red-900/20'
                      }`}
                    >
                      {row.isValid ? (
                        <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 shrink-0 mt-0.5" />
                      ) : (
                        <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-gray-700 dark:text-gray-300">
                          Row {row.rowNumber}
                        </p>
                        {row.error && (
                          <p className="text-xs text-red-600 dark:text-red-400 mt-1">{row.error}</p>
                        )}
                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 truncate">
                          {Object.entries(row.data)
                            .slice(0, 2)
                            .map(([k, v]) => `${k}: ${v}`)
                            .join(' • ')}
                        </p>
                      </div>
                    </div>
                  ))}
                  {rows.length > 10 && (
                    <div className="p-3 bg-gray-50 dark:bg-gray-700/50 text-xs text-gray-500 dark:text-gray-400">
                      ... and {rows.length - 10} more rows
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="animate-in fade-in-0 zoom-in-95 duration-300">
            <div className="text-center py-6">
              {importStats.failed > 0 ? (
                <AlertCircle className="w-12 h-12 text-amber-600 dark:text-amber-400 mx-auto mb-3" />
              ) : (
                <CheckCircle className="w-12 h-12 text-green-600 dark:text-green-400 mx-auto mb-3" />
              )}
              <p className="text-lg font-medium text-gray-900 dark:text-white mb-2">Import complete</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {importStats.created} created{importStats.failed > 0 ? ` • ${importStats.failed} failed` : ''}
              </p>
            </div>
            {importFailures.length > 0 && (
              <div className="text-left">
                <p className="text-sm font-medium text-gray-900 dark:text-white mb-2">Failed rows</p>
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden max-h-64 overflow-y-auto">
                  {importFailures.map((failure) => (
                    <div
                      key={failure.rowNumber}
                      className="p-3 border-b border-gray-200 dark:border-gray-700 last:border-b-0 flex items-start gap-3 bg-red-50 dark:bg-red-900/20"
                    >
                      <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-gray-800 dark:text-gray-200">
                          Row {failure.rowNumber}
                          {failure.label ? ` — ${failure.label}` : ''}
                        </p>
                        <p className="text-xs text-red-600 dark:text-red-400 mt-0.5">{failure.error}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="border-t border-gray-200 dark:border-gray-700 mt-6 pt-4 flex justify-end gap-3">
          {!importComplete ? (
            <>
              <Button
                variant="secondary"
                onClick={handleClose}
                disabled={importing}
              >
                Cancel
              </Button>
              <Button
                onClick={handleImportClick}
                disabled={importing || rows.length === 0 || validRowCount === 0}
              >
                {importing ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin mr-2" />
                    Importing...
                  </>
                ) : (
                  `Import ${validRowCount} Row${validRowCount !== 1 ? 's' : ''}`
                )}
              </Button>
            </>
          ) : (
            <Button onClick={handleClose}>
              Close
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
};
